import React from 'react';
import { Check } from 'lucide-react';
import { FormStep } from '../types/form.types';
import { FormTheme } from '../types/formTheme';

interface FormStepperProps {
    steps: FormStep[];
    currentStepIndex: number;
    completedSteps?: number[];
    appearance?: FormTheme;
    className?: string;
}

const FormStepper: React.FC<FormStepperProps> = ({
    steps,
    currentStepIndex,
    completedSteps = [],
    appearance = 'default',
    className = ''
}) => {
    const sortedSteps = [...steps].sort((a, b) => a.order - b.order);

    return (
        <div className={`form-stepper ${className}`}>
            {/* Mobile: Show current step only */}
            <div className="md:hidden mb-6">
                <div className="text-sm text-gray-600 mb-2">
                    Step {currentStepIndex + 1} of {steps.length}
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                        className={`h-full transition-all duration-500 ease-out ${appearance === 'tracking' ? 'bg-orange' : 'bg-gradient-to-r from-blue-500 to-purple-500'}`}
                        style={{
                            width: `${((currentStepIndex + 1) / steps.length) * 100}%`
                        }}
                    />
                </div>
            </div>

            {/* Desktop: Show all steps */}
            <div className="hidden md:flex items-start justify-between mb-8">
                {sortedSteps.map((step, index) => {
                    const isCompleted = completedSteps.includes(index);
                    const isCurrent = index === currentStepIndex;
                    const isPast = index < currentStepIndex;

                    return (
                        <React.Fragment key={step.stepId}>
                            {/* Step indicator */}
                            <div className="w-24 flex flex-col items-center gap-3 relative z-10">
                                <div
                                    className={`
                                        w-12 h-12 rounded-full flex items-center justify-center
                                        font-semibold transition-all duration-300
                                        ${isCurrent
                                            ? appearance === 'tracking'
                                                ? 'bg-black text-white shadow-sm scale-105'
                                                : 'bg-gradient-to-br from-blue-500 to-purple-500 text-white shadow-lg scale-110'
                                            : isPast || isCompleted
                                                ? appearance === 'tracking'
                                                    ? 'bg-orange text-white'
                                                    : 'bg-green-500 text-white'
                                                : 'bg-gray-200 text-gray-500'
                                        }
                                    `}
                                >
                                    {isPast || isCompleted ? (
                                        <Check size={24} strokeWidth={3} />
                                    ) : (
                                        <span>{index + 1}</span>
                                    )}
                                </div>
                                <div className="text-center px-2">
                                    <div
                                        className={`
                                            text-sm font-medium transition-colors
                                            ${isCurrent
                                                ? appearance === 'tracking' ? 'text-black' : 'text-blue-600'
                                                : isPast || isCompleted
                                                    ? 'text-gray-900'
                                                    : 'text-gray-400'
                                            }
                                        `}
                                    >
                                        {step.title}
                                    </div>
                                </div>
                            </div>

                            {/* Connector line */}
                            {index < sortedSteps.length - 1 && (
                                <div className="flex-1 h-1 mx-4 relative mt-[23px]">
                                    <div className="absolute inset-0 bg-gray-200 rounded-full" />
                                    <div
                                        className={`
                                            absolute inset-0 rounded-full transition-all duration-500
                                            ${index < currentStepIndex
                                                ? appearance === 'tracking' ? 'bg-orange w-full' : 'bg-gradient-to-r from-green-500 to-green-500 w-full'
                                                : 'w-0'
                                            }
                                        `}
                                    />
                                </div>
                            )}
                        </React.Fragment>
                    );
                })}
            </div>
        </div>
    );
};

export default FormStepper;
